import { Navigate, type RouteObject } from 'react-router';
import { lazyComponent } from '@/platform/lazyComponent';
import { Require } from '@/platform/Require';
import { PATHWAY_SLUG, RETURN_TEMPLATES_SLUG, TEMPLATES_SLUG } from './areas';
import { LISTS } from './lists';

const EntryFormPage = lazyComponent(() => import('./EntryFormPage').then((module) => module.EntryFormPage));
const EntryListPage = lazyComponent(() => import('./EntryListPage').then((module) => module.EntryListPage));
const PathwayFormPage = lazyComponent(() => import('./PathwayFormPage').then((module) => module.PathwayFormPage));
const PathwayListPage = lazyComponent(() => import('./PathwayListPage').then((module) => module.PathwayListPage));
const PhysioConfigLayout = lazyComponent(() => import('./PhysioConfigLayout').then((module) => module.PhysioConfigLayout));
const ReturnTemplateFormPage = lazyComponent(() => import('./ReturnTemplateFormPage').then((module) => module.ReturnTemplateFormPage));
const ReturnTemplateListPage = lazyComponent(() => import('./ReturnTemplateListPage').then((module) => module.ReturnTemplateListPage));
const TemplateFormPage = lazyComponent(() => import('./TemplateFormPage').then((module) => module.TemplateFormPage));
const TemplateListPage = lazyComponent(() => import('./TemplateListPage').then((module) => module.TemplateListPage));

const CHANGE = 'physio_config.manage';

/**
 * Physio → Configuration: one menu, and for each thing it configures a list at `<slug>` and a form at `<slug>/<id>` (`<slug>/new` for a new one).
 * Anyone who can see injuries can look; adding needs the permission to change the lists (the service checks it too).
 */
export const configurationRoutes: RouteObject = {
  path: 'physio/configuration',
  element: (
    <Require permission="injury.view">
      <PhysioConfigLayout />
    </Require>
  ),
  children: [
    { index: true, element: <Navigate to={PATHWAY_SLUG} replace /> },
    { path: PATHWAY_SLUG, element: <PathwayListPage /> },
    { path: `${PATHWAY_SLUG}/new`, element: <Require permission={CHANGE}><PathwayFormPage /></Require> },
    { path: `${PATHWAY_SLUG}/:id`, element: <PathwayFormPage /> },
    ...LISTS.filter((definition) => definition.kind !== 'exercises').flatMap((definition) => [
      { path: definition.kind, element: <EntryListPage key={definition.kind} definition={definition} /> },
      { path: `${definition.kind}/new`, element: <Require permission={CHANGE}><EntryFormPage key={definition.kind} definition={definition} /></Require> },
      { path: `${definition.kind}/:id`, element: <EntryFormPage key={definition.kind} definition={definition} /> },
    ]),
    { path: RETURN_TEMPLATES_SLUG, element: <ReturnTemplateListPage /> },
    { path: `${RETURN_TEMPLATES_SLUG}/new`, element: <Require permission={CHANGE}><ReturnTemplateFormPage /></Require> },
    { path: `${RETURN_TEMPLATES_SLUG}/:id`, element: <ReturnTemplateFormPage /> },
    { path: TEMPLATES_SLUG, element: <TemplateListPage /> },
    { path: `${TEMPLATES_SLUG}/new`, element: <Require permission={CHANGE}><TemplateFormPage /></Require> },
    { path: `${TEMPLATES_SLUG}/:id`, element: <TemplateFormPage /> },
  ],
};
